import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    if (import.meta.env.DEV) console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ maxWidth: '400px', width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', background: 'rgba(239,68,68,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>

            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#f1f5f9', marginBottom: '8px' }}>
              Something went wrong
            </h1>

            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '24px' }}>
              We encountered an unexpected error. Please try again or refresh the page.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div style={{ background: '#0f172a', borderRadius: '8px', padding: '12px', marginBottom: '24px', textAlign: 'left', overflow: 'auto', maxHeight: '128px' }}>
                <p style={{ fontSize: '12px', fontFamily: 'monospace', color: '#f87171', margin: 0 }}>
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={this.handleRetry}
                style={{ flex: 1, background: '#334155', color: '#f1f5f9', fontWeight: 600, padding: '12px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                style={{ flex: 1, background: '#3b82f6', color: '#fff', fontWeight: 600, padding: '12px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
